import { AppLinkService } from "lib/services/app-link-service";
import { GetAssetStatusV1Output } from "lib/types/app-link";
import { useCallback, useEffect, useMemo, useState } from "react";

interface UseAssetsProps {
  itemId?: string;
  productName?: string;
  instanceName?: string;
  assetCategory?: "SOFTWARE" | "HARDWARE";
  skipValidation?: boolean;
}

interface UseAssetsReturn {
  assetStatus: GetAssetStatusV1Output | undefined;
  effectiveHasLicense: boolean;
  isLoading: boolean;
  error: Error | null;
  checkAssetStatus: () => Promise<void>;
  returnAsset: () => Promise<void>;
  getEffectiveLicenseStatus: (status: GetAssetStatusV1Output | undefined, productName?: string) => boolean;
}

/**
 * Hook to manage asset operations using AppLinkService
 *
 * @param {UseAssetsProps} props - Configuration for the hook
 * @returns {UseAssetsReturn} Asset status and operations
 */
export const useAssets = ({
  itemId,
  productName,
  instanceName,
  assetCategory = "SOFTWARE",
  skipValidation = true, // by default, products are NOT returnables
}: UseAssetsProps): UseAssetsReturn => {
  const [assetStatus, setAssetStatus] = useState<GetAssetStatusV1Output>();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);

  // Memoize the AppLinkService instance to avoid recreating it on each render
  const appLinkService = useMemo(() => new AppLinkService(), []);

  // Memoize the input parameters to avoid unnecessary API calls
  const assetStatusParams = useMemo(
    () => ({
      productName,
      instanceName,
      assetCategory,
      skipValidation,
    }),
    [productName, instanceName, assetCategory, skipValidation]
  );

  /**
   * Check the license status of an asset
   */
  const checkAssetStatus = useCallback(async () => {
    // Skip validation if product name is missing or skipValidation is true
    if (!productName || skipValidation) {
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const result = await appLinkService.checkLicenseStatus({
        assetCategory,
        productName,
        instanceName,
      });

      setAssetStatus(result);
    } catch (err) {
      // Handle license check failure
      setError(err instanceof Error ? err : new Error("Failed to check license status"));
      setAssetStatus({
        hasLicense: false,
      });
    } finally {
      setIsLoading(false);
    }
  }, [appLinkService, assetStatusParams, productName, instanceName, assetCategory, skipValidation]);

  /**
   * Return an asset
   */
  const returnAsset = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      await appLinkService.startAssetReturnProcess({
        itemId,
        productName,
        instanceName,
        assetCategory,
      });

      // Update asset status after return
      await checkAssetStatus();
    } catch (err) {
      // Handle asset return failure
      setError(err instanceof Error ? err : new Error("Failed to return asset"));
    } finally {
      setIsLoading(false);
    }
  }, [appLinkService, itemId, productName, instanceName, assetCategory, checkAssetStatus]);

  // Check asset status on mount and when dependencies change
  useEffect(() => {
    let isMounted = true;

    const fetchAssetStatus = async () => {
      if (isMounted) {
        await checkAssetStatus();
      }
    };

    // Execute immediately
    fetchAssetStatus();

    // Cleanup function to prevent state updates on unmounted component
    return () => {
      isMounted = false;
    };
  }, [productName, instanceName, assetCategory, skipValidation]);

  /**
   * Determines if the user has an effective license for UI purposes.
   * For Smartsheet products:
   * - licensed_user: treated as having license
   * - connected_user: treated as no license
   * - any future license types: default to connected_user behavior (no license)
   *
   * For other products: use hasLicense as-is
   */
  const getEffectiveLicenseStatus = useCallback((status: GetAssetStatusV1Output | undefined, productName?: string) => {
    if (productName === "Smartsheet" && status?.hasLicense) {
      return status.licenseType === "licensed_user";
    }
    return status?.hasLicense || false;
  }, []);

  // Calculate effective license status for UI components
  const effectiveHasLicense = useMemo(() => {
    return getEffectiveLicenseStatus(assetStatus, productName);
  }, [assetStatus, productName, getEffectiveLicenseStatus]);

  return {
    assetStatus,
    effectiveHasLicense,
    isLoading,
    error,
    checkAssetStatus,
    returnAsset,
    getEffectiveLicenseStatus,
  };
};
